import Colors from '@constants/Colors';
import EventDateDisplay from '@molecules/EventDateDisplay';
import type { Event, Ticket } from '@sunrise-ui/api/events';
import { ExperienceType } from '@sunrise-ui/api/events';
import {
  Button,
  ButtonVariants,
  Card,
  TextBold,
  TextReg,
  useAuthStore,
  useDynamicStyles,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import { DateTime } from 'luxon';
import React from 'react';
import { StyleSheet, View, type ViewStyle } from 'react-native';

type Props = {
  ticket: Ticket;
  event: Event;
  onViewTicket: () => void;
  style?: ViewStyle;
  userId?: string;
};

export default function TicketCard({
  event,
  onViewTicket,
  style,
  userId,
}: Props) {
  const { currentUserId } = useAuthStore();
  const styles = useDynamicStyles(createStyles);
  const startDate = DateTime.fromISO(event.start_date ?? '');
  const isVirtual = event.experience_type !== ExperienceType.InPerson;

  return (
    <Card style={[styles.ticketCard, style]}>
      <View style={styles.topRow}>
        <EventDateDisplay startDate={startDate} />
        {currentUserId === userId && (
          <View style={styles.buttonContainer}>
            <Button
              size="small"
              variant={ButtonVariants.WHITE}
              onPress={onViewTicket}
              labelStyle={styles.buttonLabel}
            >
              <TextReg>View Ticket</TextReg>
            </Button>
          </View>
        )}
      </View>

      <View style={styles.eventInfo}>
        <TextBold style={styles.title} numberOfLines={2}>
          {event.name}
        </TextBold>
        {event.description && (
          <TextReg style={styles.description} numberOfLines={3}>
            {event.description}
          </TextReg>
        )}
        <TextReg style={styles.eventType}>
          {isVirtual ? 'Virtual Event' : 'In-Person Event'}
        </TextReg>
      </View>
    </Card>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    ticketCard: {
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      minWidth: 200,
      maxWidth: isMobile ? undefined : 400,
      minHeight: isMobile ? 200 : 240,
      flex: 1,
      padding: isMobile ? 16 : 20,
    },
    topRow: {
      display: 'flex',
      flexDirection: 'row',
      gap: isMobile ? 12 : 20,
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      width: '100%',
      marginBottom: isMobile ? 12 : 16,
    },
    eventInfo: {
      gap: isMobile ? 8 : 12,
      width: '100%',
      flex: 1,
    },
    title: {
      fontSize: isMobile ? 18 : 22,
    },
    description: {
      fontSize: isMobile ? 14 : 16,
      color: Colors.dark.text,
      opacity: 0.8,
    },
    eventType: {
      fontSize: isMobile ? 12 : 14,
      color: Colors.dark.text,
      opacity: 0.6,
      marginTop: 'auto',
    },
    buttonContainer: {
      alignItems: 'flex-end',
    },
    buttonLabel: {
      marginHorizontal: 12,
    },
  });
